/**
 * GroceryTour — the grocery wiring for the SHARED guided-tour engine.
 *
 * Holds no tour mechanics (those live in components/shared/GuidedTour.jsx) and
 * no copy (that lives in the en/fr/es locale files). What it owns:
 *   · who has seen it — per MEMBER, on the server, because a fridge card is a
 *     shared device and localStorage cannot tell a parent from a child;
 *   · when it may auto-open — never in a demo organization, never twice;
 *   · French tu -> vous, through the SAME converter the chefs use;
 *   · the little celebration at the end.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuidedTour } from '../shared/GuidedTour';
import { getStoredUser } from '../../lib/session';
import CelebrationBurst from '../engagement/CelebrationBurst';
import {
    TOUR_AVOID, TOUR_LABEL_KEYS, TOUR_MASCOT, groceryTourSteps,
} from './groceryTour';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

const call = (path, session, body) => fetch(`${API}/api/grocery${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
    body: body ? JSON.stringify(body) : undefined,
}).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`))));

/** Server-side "has this member seen it", plus the one auto-open. */
export function useGroceryTour(session) {
    const [state, setState] = useState(null);
    const [open, setOpen] = useState(false);
    const autoDone = useRef(false);

    const reload = useCallback(() => {
        if (!session) return;
        call('/tour', session).then(setState).catch(() => setState({ seen: true, auto_open: false }));
    }, [session]);
    useEffect(() => { reload(); }, [reload]);

    useEffect(() => {
        // A real customer gets it once, after the claim. A demo organization and
        // a super admin NEVER get it automatically — a sales demo is not
        // interrupted. It starts from the "?" button there.
        //
        // THE ACCOUNT-LEVEL SWITCH: a fridge card has no logged-in person, so
        // `show_guided_tour` can only belong to PORTAL staff looking at a
        // customer surface in their own browser. Whenever a portal user is
        // signed in here — any role — nothing auto-opens, and if that user has
        // the switch off the "?" button is their only way in, exactly as it is
        // in the Command Center. Per MEMBER is the customer-side rule and it
        // lives on the server (member_key), not in this browser.
        const staff = (() => { try { return getStoredUser(); } catch (_e) { return null; } })();
        if (staff) return undefined;
        if (!state?.auto_open || state.seen || autoDone.current) return undefined;
        autoDone.current = true;
        const timer = setTimeout(() => setOpen(true), 900);
        return () => clearTimeout(timer);
    }, [state]);

    const start = useCallback(() => setOpen(true), []);
    const close = useCallback(() => setOpen(false), []);
    return { state, open, start, close, reload };
}

export default function GroceryTour({ session, kid = false, flags, state, open, onClose,
    onStep, onFinished, goTo = null, steps: override = null, tourKey = 'main',
    cheersOn = true }) {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [vous, setVous] = useState(null);
    const [cheer, setCheer] = useState(false);
    // The 3-step "Send your list" mini-tour passes its own steps (SEND_STEPS).
    // Everything else — the engine, the French converter, the cheers, the
    // per-member memory — is shared with the big tour, byte for byte.
    const steps = override || groceryTourSteps({ kid, flags });

    // French is WRITTEN in "tu" in the locale files. When the organization asks
    // for "vous", the lines go through services/chef_voice.to_vous — one French
    // source of truth, no second set of strings to drift.
    useEffect(() => {
        if (!open || lang !== 'fr' || state?.address_form !== 'vous') { setVous(null); return; }
        const lines = steps.map((s) => t(s.titleKey));
        call('/tour/vous', session, { lines })
            .then((r) => setVous(r.lines || null))
            .catch(() => setVous(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lang, state?.address_form, session, kid]);

    const resolved = steps.map((s, idx) => ({ ...s, text: vous?.[idx] || t(s.titleKey) }));

    const finished = useRef(false);
    const finish = async (reason) => {
        finished.current = true;
        try {
            await call('/tour/seen', session, { finished: reason === 'done', tour: tourKey });
        } catch (_e) { /* ignore */ }
        if (tourKey === 'main') {
            try { await call('/tour/practice/clear', session, {}); } catch (_e) { /* ignore */ }
        }
        if (reason === 'done') setCheer(true);
        onFinished?.(reason);
    };

    // Tapping the dim area is also a way out, and it must tidy up the same way.
    const handleClose = () => {
        if (!finished.current) finish('skip');
        finished.current = false;
        onClose();
    };

    return (
        <>
            <CelebrationBurst show={cheer} message={t('groceryTour.cheer')}
                onDone={() => setCheer(false)} testId="grocery-tour-cheer" />
            <GuidedTour steps={resolved} open={open} onClose={handleClose}
                tapToAdvance variant="playful" mascot={TOUR_MASCOT} avoid={TOUR_AVOID}
                labelKeys={TOUR_LABEL_KEYS} hideDontShow tapHereKey="groceryTour.tapHere"
                cheers={cheersOn ? t('groceryTour.cheers', { returnObjects: true }) : null}
                onStep={onStep} onFinish={finish} goTo={goTo} />
        </>
    );
}
